import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Car, Phone, EnvelopeSimple, MapPin, Globe, Spinner, CheckCircle } from '@phosphor-icons/react';

const getApiBase = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  // If the bundled env URL is from a *different* preview deployment than
  // the page we're loaded on, prefer the page's own origin — the ingress
  // routes /api to the local backend regardless of subdomain.
  if (envUrl) {
    try {
      const envHost = new URL(envUrl).hostname;
      if (envHost !== window.location.hostname) {
        return window.location.origin;
      }
    } catch { /* malformed env, ignore */ }
  }
  return envUrl || window.location.origin;
};
const API = getApiBase();

const fmtDZD = (n) => {
  if (n === null || n === undefined || n === '' || isNaN(Number(n))) return null;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(n)) + ' DZD';
};

const fileUrl = (id) => (id?.startsWith?.('http') ? id : `${API}/api/files/${id}`);

const PublicCatalogPage = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState(null);
  const [filterBrand, setFilterBrand] = useState('all');
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteModel, setQuoteModel] = useState(null);
  const [quoteVariantId, setQuoteVariantId] = useState('');
  const [quoteForm, setQuoteForm] = useState({ full_name: '', phone: '', email: '', message: '' });
  const [quoteSending, setQuoteSending] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);

  useEffect(() => {
    document.title = 'Catalog';
    axios
      .get(`${API}/api/public/catalog/${encodeURIComponent(slug)}`)
      .then((r) => {
        setData(r.data);
        if (r.data?.company?.name) document.title = `${r.data.company.name} — Catalog`;
      })
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
        else toast.error('Failed to load catalog');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const openQuote = (model, variantId = '') => {
    setQuoteModel(model);
    setQuoteVariantId(variantId || (model.variants?.[0]?.id ?? ''));
    setQuoteForm({ full_name: '', phone: '', email: '', message: '' });
    setQuoteSent(false);
    setQuoteOpen(true);
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    if (quoteSending) return;
    if (!quoteForm.full_name.trim() || !quoteForm.phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    setQuoteSending(true);
    try {
      await axios.post(`${API}/api/public/catalog/${encodeURIComponent(slug)}/quote-request`, {
        model_id: quoteModel.id,
        variant_id: quoteVariantId || null,
        full_name: quoteForm.full_name.trim(),
        phone: quoteForm.phone.trim(),
        email: quoteForm.email.trim() || null,
        message: quoteForm.message.trim() || null,
      });
      setQuoteSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setQuoteSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30">
        <Spinner className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-6">
        <div className="text-center max-w-md">
          <Car weight="duotone" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Catalog not found</h1>
          <p className="text-muted-foreground">The link is invalid or the catalog has been deactivated.</p>
        </div>
      </div>
    );
  }

  const company = data?.company || {};
  const models = data?.models || [];
  const brands = Array.from(new Map(models.map(m => [m.brand_id, { id: m.brand_id, name: m.brand_name, logo: m.brand_logo }])).values());
  const filtered = filterBrand === 'all' ? models : models.filter(m => m.brand_id === filterBrand);

  return (
    <div className="min-h-screen bg-secondary/20" data-testid="public-catalog-page">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {company.logo_url ? (
              <img src={fileUrl(company.logo_url)} alt={company.name} className="w-12 h-12 object-contain bg-secondary p-1" />
            ) : (
              <div className="w-12 h-12 bg-primary/10 flex items-center justify-center">
                <Car weight="duotone" className="w-6 h-6 text-primary" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{company.brand_name || company.name}</h1>
              <p className="text-sm text-muted-foreground">Available models & prices</p>
            </div>
          </div>
          <div className="text-sm space-y-1 text-muted-foreground">
            {(company.phone || company.support_phone) && (
              <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {company.phone || company.support_phone}</div>
            )}
            {(company.email || company.support_email) && (
              <div className="flex items-center gap-1.5"><EnvelopeSimple className="w-3.5 h-3.5" /> {company.email || company.support_email}</div>
            )}
            {company.address && (
              <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {company.address}</div>
            )}
            {company.website && (
              <div className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> <a href={company.website} className="hover:underline" target="_blank" rel="noopener noreferrer">{company.website}</a></div>
            )}
          </div>
        </div>
      </header>

      {/* Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        {brands.length > 1 && (
          <div className="flex items-center gap-2">
            <Label className="text-sm">Brand:</Label>
            <Select value={filterBrand} onValueChange={setFilterBrand}>
              <SelectTrigger className="w-56" data-testid="catalog-brand-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All brands ({models.length})</SelectItem>
                {brands.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name} ({models.filter(m => m.brand_id === b.id).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Car weight="duotone" className="w-12 h-12 mx-auto mb-3" />
            <p>No models available right now. Please check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((m) => {
              const priceFrom = m.price_from;
              const priceTo = m.price_to;
              return (
                <article
                  key={m.id}
                  className="bg-card border border-border overflow-hidden flex flex-col hover:shadow-md transition-shadow"
                  data-testid={`catalog-model-${m.id}`}
                >
                  <div className="aspect-video bg-secondary/50 flex items-center justify-center">
                    {m.image_url ? (
                      <img src={fileUrl(m.image_url)} alt={m.name} className="w-full h-full object-contain" />
                    ) : (
                      <Car weight="duotone" className="w-16 h-16 text-muted-foreground" />
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">{m.brand_name}</div>
                    <h3 className="font-bold text-lg mt-0.5">{m.name}</h3>
                    {m.years?.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Available years: {m.years.join(', ')}
                      </div>
                    )}
                    <div className="mt-3 mb-4">
                      <div className="text-[11px] uppercase text-muted-foreground tracking-wide">
                        {priceFrom === priceTo ? 'Price' : 'From'}
                      </div>
                      <div className="text-xl font-bold text-primary">
                        {fmtDZD(priceFrom)}
                        {priceFrom !== priceTo && (
                          <span className="text-sm text-muted-foreground font-normal">
                            {' '}to {fmtDZD(priceTo)}
                          </span>
                        )}
                      </div>
                    </div>
                    {m.variants?.length > 1 && (
                      <details className="text-sm text-muted-foreground mb-3">
                        <summary className="cursor-pointer hover:text-foreground">
                          {m.variants.length} variants
                        </summary>
                        <ul className="mt-2 space-y-1">
                          {m.variants.map(v => (
                            <li key={v.id} className="flex justify-between gap-2 py-1">
                              <span>{v.name}</span>
                              <span className="font-medium">{fmtDZD(v.price)}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                    <Button
                      onClick={() => openQuote(m)}
                      className="btn-primary mt-auto w-full"
                      data-testid={`request-quote-${m.id}`}
                    >
                      Request a Quote
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        Prices in DZD. Quotes are non-binding. {company.brand_name || company.name} © {new Date().getFullYear()}
      </footer>

      {/* Quote Dialog */}
      <Dialog open={quoteOpen} onOpenChange={(o) => { if (!quoteSending) setQuoteOpen(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {quoteSent ? 'Request received' : `Request a quote — ${quoteModel?.brand_name} ${quoteModel?.name}`}
            </DialogTitle>
            <DialogDescription>
              {quoteSent
                ? 'Thank you! Our team will reach out to you shortly with availability and pricing.'
                : 'Fill in your details — we will contact you to confirm availability and the final price.'}
            </DialogDescription>
          </DialogHeader>
          {quoteSent ? (
            <div className="py-6 text-center">
              <CheckCircle weight="duotone" className="w-16 h-16 text-green-600 mx-auto mb-3" />
              <Button onClick={() => setQuoteOpen(false)} data-testid="quote-close-btn" className="btn-primary">Close</Button>
            </div>
          ) : (
            <form onSubmit={submitQuote} className="space-y-3">
              {quoteModel?.variants?.length > 1 && (
                <div className="space-y-1.5">
                  <Label>Variant</Label>
                  <Select value={quoteVariantId} onValueChange={setQuoteVariantId}>
                    <SelectTrigger data-testid="quote-variant-select"><SelectValue placeholder="Select a variant" /></SelectTrigger>
                    <SelectContent>
                      {quoteModel.variants.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name} {v.price ? `— ${fmtDZD(v.price)}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Full name *</Label>
                <Input
                  required
                  value={quoteForm.full_name}
                  onChange={(e) => setQuoteForm(f => ({ ...f, full_name: e.target.value }))}
                  data-testid="quote-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>Phone *</Label>
                  <Input
                    required
                    value={quoteForm.phone}
                    onChange={(e) => setQuoteForm(f => ({ ...f, phone: e.target.value }))}
                    data-testid="quote-phone"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={quoteForm.email}
                    onChange={(e) => setQuoteForm(f => ({ ...f, email: e.target.value }))}
                    data-testid="quote-email"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Message (optional)</Label>
                <Textarea
                  rows={3}
                  value={quoteForm.message}
                  onChange={(e) => setQuoteForm(f => ({ ...f, message: e.target.value }))}
                  data-testid="quote-message"
                  placeholder="Preferred year, color, payment plan, etc."
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setQuoteOpen(false)} disabled={quoteSending}>Cancel</Button>
                <Button type="submit" className="btn-primary" disabled={quoteSending} data-testid="quote-submit">
                  {quoteSending ? 'Sending…' : 'Send request'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicCatalogPage;
